import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import clsx from "clsx";
import {
  ArrowPathIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  PlusIcon,
  TagIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { useToast } from "@/components/ui/toast/useToast";
import { ApiError } from "@/lib/apiClient";
import {
  getContractCampaigns,
  getMaintenanceContract,
  isOnCallContract,
  maintenanceContractCustomerLabel,
  openMaintenanceContractDocument,
  WARTUNGEN_REALTIME_DOMAINS,
  type MaintenanceCampaign,
  type MaintenanceContract,
} from "@/lib/wartungen";
import { useCurrentUser, userIsBuero } from "@/lib/auth";
import { useRealtimeSync } from "@/lib/useRealtime";
import { ContractStatusBadge } from "./components/ContractCard";
import { MaintenanceCampaignCard } from "./components/MaintenanceCampaignCard";
import {
  buildCampaignView,
  contractCategoryLabels,
  contractEditHref,
  contractIntervalLabel,
  contractLocationLabels,
  contractRenewalLabel,
  contractRelationLabel,
  contractRuntimeLabel,
  contractSubtitle,
  contractTitle,
  dedupeOptions,
  FILTER_ALL,
  type CampaignView,
} from "./lib/campaignView";
import { useCampaignAverageTimes } from "./lib/useCampaignAverageTimes";
import { NeueWartungDialog } from "./NeueWartungDialog";

function FactRow({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0 text-sm">
      <dt className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-label-tertiary">
        <span className="shrink-0" aria-hidden>
          {icon}
        </span>
        {label}
      </dt>
      <dd className="mt-0.5 text-label">{children}</dd>
    </div>
  );
}

function Chips({
  label,
  options,
  selected,
  onSelect,
}: {
  label: string;
  options: { key: string; label: string }[];
  selected: string;
  onSelect: (key: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="mr-1 text-xs font-semibold uppercase tracking-wide text-label-tertiary">{label}</span>
      {[{ key: FILTER_ALL, label: "Alle" }, ...options].map((opt) => (
        <button
          key={opt.key}
          type="button"
          aria-pressed={selected === opt.key}
          className={clsx(
            "rounded-full px-3 py-1 text-sm font-medium transition",
            selected === opt.key
              ? "bg-accent text-label-on-accent"
              : "bg-fill-tertiary text-label-secondary hover:bg-fill-secondary hover:text-label",
          )}
          onClick={() => onSelect(opt.key)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export function VertragsWartungenPage() {
  const [params] = useSearchParams();
  const contractId = (params.get("contract_id") || "").trim();

  const toast = useToast();
  const { user } = useCurrentUser();
  const isBuero = userIsBuero(user);
  const [contract, setContract] = useState<MaintenanceContract | null>(null);
  const [campaigns, setCampaigns] = useState<MaintenanceCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const [year, setYear] = useState(FILTER_ALL);
  const [location, setLocation] = useState(FILTER_ALL);

  const load = useCallback(async (options?: { silent?: boolean }) => {
    if (!contractId) {
      setLoading(false);
      setError("Es wurde kein Vertrag angegeben (contract_id fehlt).");
      return;
    }
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      const [c, camps] = await Promise.all([getMaintenanceContract(contractId), getContractCampaigns(contractId)]);
      setContract(c);
      setCampaigns(camps);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Vertragsdaten konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => {
    void load();
  }, [load]);

  useRealtimeSync({
    domains: [...WARTUNGEN_REALTIME_DOMAINS],
    enabled: !!contractId,
    onChange: () => void load({ silent: true }),
  });

  const views: CampaignView[] = useMemo(() => {
    if (!contract) return [];
    return campaigns
      .map((c, i) => buildCampaignView(c, contract, i))
      .sort((a, b) => {
        if (a.year !== b.year) return b.year.localeCompare(a.year, "de", { numeric: true });
        return a.title.localeCompare(b.title, "de");
      });
  }, [campaigns, contract]);

  const averageByCampaign = useCampaignAverageTimes(views, isBuero);

  const yearOptions = useMemo(
    () =>
      Array.from(new Set(views.map((v) => v.year).filter((y) => y && y !== "–")))
        .sort((a, b) => b.localeCompare(a, "de", { numeric: true }))
        .map((y) => ({ key: y, label: y })),
    [views],
  );
  const locationOptions = useMemo(() => dedupeOptions(views.map((v) => v.location)), [views]);

  const filtered = useMemo(
    () =>
      views.filter(
        (v) => (year === FILTER_ALL || v.year === year) && (location === FILTER_ALL || v.location.key === location),
      ),
    [views, year, location],
  );

  const groupedByYear = useMemo(() => {
    const map = new Map<string, CampaignView[]>();
    for (const v of filtered) {
      const list = map.get(v.year) ?? [];
      list.push(v);
      map.set(v.year, list);
    }
    return Array.from(map.entries()).sort(([a], [b]) => b.localeCompare(a, "de", { numeric: true }));
  }, [filtered]);

  const onCall = isOnCallContract(contract);
  const title = contract ? contractTitle(contract) : contractId ? `Vertrag #${contractId}` : "Vertrag";
  const subtitleNumber = contract ? contractSubtitle(contract) : "";
  const categories = contract ? contractCategoryLabels(contract) : [];
  const locations = contract ? contractLocationLabels(contract) : [];
  const renewal = contract ? contractRenewalLabel(contract) : "";
  const canCreate = Boolean(contract) && categories.length > 0;
  const hasDocument = Boolean(contract?.document) && Boolean(contractId);

  function openDocument() {
    if (!contractId || pdfBusy) return;
    setPdfBusy(true);
    void openMaintenanceContractDocument(contractId, { filename: contract?.document?.filename })
      .catch((err: unknown) => toast.error(err instanceof Error ? err.message : "PDF konnte nicht geöffnet werden."))
      .finally(() => setPdfBusy(false));
  }

  return (
    <AppShell
      title={title}
      subtitle={
        contract
          ? [
              subtitleNumber,
              onCall ? "Auf Zuruf" : "Wartungsvertrag",
              maintenanceContractCustomerLabel(contract) || "Kunde unbekannt",
            ]
              .filter(Boolean)
              .join(" · ")
          : undefined
      }
      back={{
        to: onCall ? "/wartungen?tab=auf-zuruf" : "/wartungen?tab=vertraege",
        label: onCall ? "zurück zu Auf Zuruf" : "zurück zu den Verträgen",
      }}
      actions={
        contractId ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              onClick={() => setWizardOpen(true)}
              disabled={!canCreate}
              leftIcon={<PlusIcon className="h-4 w-4" aria-hidden />}
              title={
                !canCreate && contract
                  ? onCall
                    ? "Bitte zuerst eine Wartungskategorie hinterlegen."
                    : "Bitte zuerst eine Wartungskategorie im Vertrag hinterlegen."
                  : undefined
              }
            >
              Neue Wartung
            </Button>
            {!onCall && hasDocument ? (
              <Button
                type="button"
                variant="secondary"
                disabled={pdfBusy}
                leftIcon={<DocumentTextIcon className="h-4 w-4" aria-hidden />}
                title={contract?.document?.filename}
                onClick={openDocument}
              >
                Vertrag (PDF)
              </Button>
            ) : null}
            <Link
              to={contractEditHref(Number(contractId))}
              className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-card px-4 text-sm font-medium text-label ring-1 ring-inset ring-separator-opaque hover:bg-fill-tertiary"
            >
              <PencilSquareIcon className="h-4 w-4" aria-hidden />
              Bearbeiten
            </Link>
          </div>
        ) : null
      }
    >
      {error ? (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <p className="flex items-center gap-2 text-sm text-label-secondary">
          <Spinner /> Lade Vertragsdaten …
        </p>
      ) : contract ? (
        <>
          <section className="rounded-ios border border-separator bg-card p-4 shadow-ios">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-label">{onCall ? "Stammdaten" : "Vertragsdaten"}</h2>
                <p className="text-sm text-label-secondary">
                  {onCall
                    ? contractRelationLabel(contract)
                    : contract.contract_number
                      ? `Nr. ${contract.contract_number}`
                      : `Vertrag #${contract.id}`}
                  {!onCall && contract.document ? (
                    <>
                      {" · "}
                      <button
                        type="button"
                        className="font-medium text-accent hover:underline disabled:opacity-50"
                        disabled={pdfBusy}
                        onClick={openDocument}
                      >
                        Vertrag als PDF
                      </button>
                    </>
                  ) : onCall ? null : (
                    <>
                      {" · "}
                      <Link to={contractEditHref(contract)} className="text-label-tertiary hover:text-accent hover:underline">
                        Noch kein Vertrags-PDF hinterlegt
                      </Link>
                    </>
                  )}
                </p>
              </div>
              <ContractStatusBadge status={contract.status} />
            </div>
            <dl className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <FactRow icon={<UserIcon className="h-4 w-4" />} label="Kunde">
                {maintenanceContractCustomerLabel(contract) || "–"}
              </FactRow>
              <FactRow icon={<BuildingOffice2Icon className="h-4 w-4" />} label={`Ort${locations.length === 1 ? "" : "e"}`}>
                {locations.length ? locations.join(", ") : "–"}
              </FactRow>
              {onCall ? null : (
                <FactRow icon={<CalendarDaysIcon className="h-4 w-4" />} label="Laufzeit">
                  {contractRuntimeLabel(contract)}
                  {renewal ? <span className="block text-xs text-label-secondary">{renewal}</span> : null}
                </FactRow>
              )}
              <FactRow icon={<ArrowPathIcon className="h-4 w-4" />} label="Rhythmus">
                <span className="block first-letter:uppercase">{contractIntervalLabel(contract)}</span>
              </FactRow>
            </dl>
            <div className="mt-4 flex flex-wrap items-center gap-1.5">
              <TagIcon className="h-4 w-4 text-label-tertiary" aria-hidden />
              {categories.length ? (
                categories.map((cat) => (
                  <span key={cat} className="rounded-full bg-fill-tertiary px-2.5 py-0.5 text-xs font-medium text-label-secondary">
                    {cat}
                  </span>
                ))
              ) : (
                <span className="text-sm text-status-orange">
                  Keine Wartungskategorie hinterlegt – bitte ergänzen, um Wartungen anlegen zu können.
                </span>
              )}
            </div>
          </section>

          <section className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-label">
                Wartungen <span className="text-sm font-normal text-label-tertiary">({views.length})</span>
              </h2>
              {views.length > 0 && (yearOptions.length > 1 || locationOptions.length > 1) ? (
                <div className="flex flex-wrap items-center gap-4">
                  {yearOptions.length > 1 ? <Chips label="Jahr" options={yearOptions} selected={year} onSelect={setYear} /> : null}
                  {locationOptions.length > 1 ? (
                    <Chips label="Ort" options={locationOptions} selected={location} onSelect={setLocation} />
                  ) : null}
                </div>
              ) : null}
            </div>

            {views.length === 0 ? (
              <div className="rounded-ios border border-dashed border-separator bg-card p-8 text-center shadow-ios">
                <p className="text-sm font-medium text-label">
                  {onCall ? "Für diesen Eintrag gibt es noch keine Wartung." : "Für diesen Vertrag gibt es noch keine Wartung."}
                </p>
                <p className="mt-1 text-sm text-label-secondary">
                  Lege die erste Wartung an: Projekt und Zeitraum wählen, danach Objekte erfassen.
                </p>
                <Button
                  type="button"
                  className="mt-4"
                  onClick={() => setWizardOpen(true)}
                  disabled={!canCreate}
                  leftIcon={<PlusIcon className="h-4 w-4" aria-hidden />}
                >
                  Erste Wartung anlegen
                </Button>
              </div>
            ) : filtered.length === 0 ? (
              <div className="rounded-ios border border-dashed border-separator bg-card p-6 text-center text-sm text-label-secondary shadow-ios">
                Keine Wartungen für die gewählten Filter.{" "}
                <button
                  type="button"
                  className="font-medium text-accent hover:underline"
                  onClick={() => {
                    setYear(FILTER_ALL);
                    setLocation(FILTER_ALL);
                  }}
                >
                  Filter zurücksetzen
                </button>
              </div>
            ) : (
              <div className="space-y-5">
                {groupedByYear.map(([groupYear, items]) => (
                  <div key={groupYear}>
                    {groupedByYear.length > 1 ? (
                      <h3 className="mb-2 text-sm font-semibold text-label-secondary">{groupYear}</h3>
                    ) : null}
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                      {items.map((view) => (
                        <MaintenanceCampaignCard
                          key={view.id}
                          view={view}
                          showCustomer={false}
                          showContract={false}
                          averagePerAsset={
                            view.campaignId != null ? averageByCampaign[view.campaignId] : null
                          }
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      ) : null}

      <NeueWartungDialog open={wizardOpen} onClose={() => setWizardOpen(false)} initialContract={contract} />
    </AppShell>
  );
}
